<template>
  <UserShell
    title="机构身份认证"
    subtitle="请上传真实材料"
    :profile="profile"
    :menu-items="menuItems"
    active-key="overview"
    center-path="/user/org"
  >
    <div v-if="isAgreementUploading" class="uploading-overlay" role="status" aria-live="polite">
      <div class="uploading-indicator">
        <i class="el-icon-loading" aria-hidden="true"></i>
        <span>协议文件上传中</span>
      </div>
    </div>

    <div class="back-row">
      <button type="button" class="back-btn" @click="$router.back()">&lt; 上一页</button>
    </div>

    <section class="form-section push-t">
      <div class="field-title">营业执照 <span class="required">*</span></div>
      <div class="tip-text push-t-10">1. 需提供三证合一、二证合一的营业执照原件扫描件或加盖公司鲜章的复印件</div>
      <div class="tip-text">2. 请提供信息清晰可见的证件照片，支持jpg、png、jpeg 格式的图片，大小不能大于5M</div>
      <button type="button" :class="['upload-license-card', { 'has-preview': businessLicensePreviewUrl }]" :disabled="isCertificationLocked || isUploading" @click="triggerUpload('businessLicense')">
        <img v-if="businessLicensePreviewUrl" class="upload-license-preview" :src="businessLicensePreviewUrl" alt="营业执照" referrerpolicy="no-referrer"/>
        <template v-else>
          <span class="plus">+</span>
          <span>{{ authForm.businessLicenseName || '点击上传图片' }}</span>
        </template>
      </button>
      <input ref="businessLicense" type="file" class="hidden-input" accept=".jpg,.jpeg,.png" @change="handleSingleFileUpload($event, 'businessLicenseUrl', 'businessLicenseName', 5)" />
    </section>

    <section class="form-section compact">
      <div class="field-title">统一社会信用代码 <span class="required">*</span></div>
      <el-input v-model.trim="authForm.companyCode" :disabled="isCertificationLocked" placeholder="请输入统一社会信用代码" class="field-control" />
      <div class="sub-tip">审核后不支持修改</div>
    </section>

    <section class="form-section compact">
      <div class="field-title">法人姓名 <span class="required">*</span></div>
      <el-input v-model.trim="authForm.legalPersonName" :disabled="isCertificationLocked" placeholder="请输入法人姓名" class="field-control" />
      <div class="sub-tip">审核后不支持修改</div>
    </section>

    <section class="form-section compact">
      <div class="field-title">注册地址 <span class="required">*</span></div>
      <el-cascader
        v-model="authForm.region"
        :options="regionOptions"
        :disabled="isCertificationLocked"
        placeholder="请选择省/市"
        class="field-control"
        @change="syncRegion"
      />
      <el-input v-model.trim="authForm.addressDetail" :disabled="isCertificationLocked" placeholder="请输入公司详细注册地址" class="field-control top-gap" />
      <div class="sub-tip">1. 以营业执照中的经营场所为准，需选择到省和市</div>
    </section>

    <section class="form-section compact">
      <div class="field-title">注册资本 <span class="required">*</span></div>
      <el-input v-model.trim="authForm.registeredCapital" :disabled="isCertificationLocked" placeholder="请填写注册金额" class="field-control" />
    </section>

    <section class="form-section">
      <div class="field-title">身份证照片 <span class="required">*</span></div>
      <div class="tip-text">1. 请上传法定代表人/经营者本人身份证照片正反面</div>
      <div class="tip-text">2. 请提供信息清晰可见的证件照片，支持jpg、png、jpeg 格式的图片，大小不能大于5M</div>
      <div class="upload-row">
        <button type="button" class="upload-image-card" :disabled="isCertificationLocked || isUploading" @click="triggerUpload('idFront')">
          <img :src="idFrontPreview" alt="身份证正面" referrerpolicy="no-referrer"/>
        </button>
        <button type="button" class="upload-image-card" :disabled="isCertificationLocked || isUploading" @click="triggerUpload('idBack')">
          <img :src="idBackPreview" alt="身份证背面" referrerpolicy="no-referrer"/>
        </button>
      </div>
      <input ref="idFront" type="file" class="hidden-input" accept=".jpg,.jpeg,.png" @change="handleSingleFileUpload($event, 'legalPersonIdCardFront', 'legalPersonIdCardFrontName', 5)" />
      <input ref="idBack" type="file" class="hidden-input" accept=".jpg,.jpeg,.png" @change="handleSingleFileUpload($event, 'legalPersonIdCardBack', 'legalPersonIdCardBackName', 5)" />
    </section>

    <section class="form-section">
      <div class="field-title">主播授权协议合同 <span class="required">*</span></div>
      <div class="tip-text">1. 需提供加盖公司鲜章的合同复印件</div>
      <div class="tip-text">2. 支持jpg、png、pdf 格式的文件，每份文件大小不能大于30M，最多上传30份文件</div>
      <button type="button" class="upload-license-card" :disabled="isCertificationLocked || isUploading || authForm.authorizationAgreements.length >= 30" @click="triggerUpload('agreement')">
        <span class="plus">+</span>
        <span>{{ isUploading ? '上传中...' : '上传协议文件' }}</span>
      </button>
      <input ref="agreement" type="file" class="hidden-input" multiple accept=".jpg,.jpeg,.png,.pdf" @change="handleAgreementUpload" />
      <div v-if="authForm.authorizationAgreements.length" class="agreement-list">
        <div v-for="(item, index) in authForm.authorizationAgreements" :key="`${item.url}-${index}`" class="agreement-item">
          <img v-if="isImageFile(item.url || item.name)" class="agreement-preview" :src="item.url" alt="协议图片" referrerpolicy="no-referrer"/>
          <span class="agreement-name">{{ item.name }}</span>
          <button type="button" class="agreement-remove" :disabled="isCertificationLocked || isUploading" @click="removeAgreement(index)">删除</button>
        </div>
      </div>
    </section>

    <div class="action-row">
      <button type="button" class="action-btn ghost" :disabled="isCertificationLocked || isSubmitting || isUploading" @click="saveDraft">保存</button>
      <button type="button" class="action-btn primary" :disabled="isCertificationLocked || isSubmitting || isUploading" @click="submitAuth">{{ submitButtonText }}</button>
    </div>
  </UserShell>
</template>

<script>
import UserShell from './components/UserShell.vue';
import sidebarBg from '@/assets/img/user/背景卡图.png';
import userAvatar from '@/assets/img/user/avatar.png';
import overviewActiveIcon from '@/assets/img/user/工作台icon-选中.png';
import talentActiveIcon from '@/assets/img/user/griconxz.png';
import talentInactiveIcon from '@/assets/img/user/gricon.png';
import assetActiveIcon from '@/assets/img/user/申请icon.png';
import assetInactiveIcon from '@/assets/img/user/申请未选中.png';
import idFrontImage from '@/assets/img/user/身份证正面.png';
import idBackImage from '@/assets/img/user/身份证背面.png';
import {
  getMemberCertification,
  getMemberProfile,
  submitMcnCertification,
  uploadMemberFile,
  uploadMemberImage
} from '@/api/user';

const AUTH_STORAGE_KEY = 'cog.org.auth';

function createMenu() {
  return [
    { key: 'overview', label: '首页概览', path: '/user/org', activeIcon: overviewActiveIcon },
    { key: 'talent', label: '主播管理', path: '/user/org/talents', activeIcon: talentActiveIcon, inactiveIcon: talentInactiveIcon },
    { key: 'asset', label: '资产评估申请', path: '/user/org/assets', activeIcon: assetActiveIcon, inactiveIcon: assetInactiveIcon }
  ];
}

function createEmptyAuthForm() {
  return {
    businessLicenseUrl: '',
    businessLicenseName: '',
    companyCode: '',
    legalPersonName: '',
    region: [],
    province: '',
    city: '',
    addressDetail: '',
    registeredCapital: '',
    legalPersonIdCardFront: '',
    legalPersonIdCardFrontName: '',
    legalPersonIdCardBack: '',
    legalPersonIdCardBackName: '',
    authorizationAgreements: [],
    status: ''
  };
}

function extractResponseData(response) {
  return response && response.data ? response.data : {};
}

function extractUploadUrl(response) {
  const data = response && typeof response === 'object' && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;

  if (typeof data === 'string') {
    return data;
  }

  if (!data || typeof data !== 'object') {
    return '';
  }

  return data.url || data.full_url || data.path || data.location || '';
}

function getFileName(value, fallback = '已上传文件') {
  if (!value) {
    return '';
  }

  if (typeof value === 'object' && value.name) {
    return value.name;
  }

  const source = String(value).split('?')[0];
  const parts = source.split('/').filter(Boolean);
  return decodeURIComponent(parts[parts.length - 1] || fallback);
}

function normalizeAgreementItems(list) {
  if (!Array.isArray(list)) {
    return [];
  }

  return list
    .map(item => {
      if (typeof item === 'string') {
        return { name: getFileName(item), url: item };
      }
      if (item && item.url) {
        return { name: item.name || getFileName(item.url), url: item.url };
      }
      return null;
    })
    .filter(Boolean);
}

function hasMeaningfulValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return value !== null && value !== undefined;
}

function pickFirstMeaningful(...values) {
  for (let index = 0; index < values.length; index += 1) {
    if (hasMeaningfulValue(values[index])) {
      return values[index];
    }
  }

  return values.length ? values[values.length - 1] : undefined;
}

function filterMeaningfulFields(data) {
  if (!data || typeof data !== 'object') {
    return {};
  }

  return Object.keys(data).reduce((result, key) => {
    if (hasMeaningfulValue(data[key])) {
      result[key] = data[key];
    }
    return result;
  }, {});
}

function getCertificationStatusLabel(status) {
  if (status === 'pending') {
    return '待审核';
  }
  if (status === 'approved') {
    return '已通过';
  }
  if (status === 'rejected') {
    return '已驳回';
  }
  return '未认证';
}

function readLocal() {
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    return raw ? JSON.parse(raw) : createEmptyAuthForm();
  } catch (error) {
    return createEmptyAuthForm();
  }
}

function saveLocal(data) {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
}

export default {
  name: 'OrganizationAuth',
  components: { UserShell },
  computed: {
    isCertificationLocked() {
      return this.authForm.status === 'pending' || this.authForm.status === 'approved';
    },
    submitButtonText() {
      if (this.authForm.status === 'approved') {
        return '已认证';
      }
      if (this.authForm.status === 'pending') {
        return '待审核';
      }
      if (this.isSubmitting) {
        return '提交中...';
      }
      if (this.authForm.status === 'rejected') {
        return '重新提交';
      }
      return '提交认证';
    },
    idFrontPreview() {
      return this.authForm.legalPersonIdCardFront || idFrontImage;
    },
    idBackPreview() {
      return this.authForm.legalPersonIdCardBack || idBackImage;
    },
    businessLicensePreviewUrl() {
      return this.isImageFile(this.authForm.businessLicenseUrl) ? this.authForm.businessLicenseUrl : '';
    }
  },
  data() {
    return {
      regionOptions: [
        { value: '北京市', label: '北京市', children: [{ value: '北京市', label: '北京市' }] },
        { value: '天津市', label: '天津市', children: [{ value: '天津市', label: '天津市' }] },
        { value: '上海市', label: '上海市', children: [{ value: '上海市', label: '上海市' }] },
        { value: '重庆市', label: '重庆市', children: [{ value: '重庆市', label: '重庆市' }] },
        { value: '四川省', label: '四川省', children: [{ value: '成都市', label: '成都市' }, { value: '绵阳市', label: '绵阳市' }, { value: '德阳市', label: '德阳市' }, { value: '乐山市', label: '乐山市' }, { value: '宜宾市', label: '宜宾市' }] },
        { value: '广东省', label: '广东省', children: [{ value: '广州市', label: '广州市' }, { value: '深圳市', label: '深圳市' }, { value: '佛山市', label: '佛山市' }, { value: '东莞市', label: '东莞市' }, { value: '珠海市', label: '珠海市' }] },
        { value: '浙江省', label: '浙江省', children: [{ value: '杭州市', label: '杭州市' }, { value: '宁波市', label: '宁波市' }, { value: '温州市', label: '温州市' }, { value: '嘉兴市', label: '嘉兴市' }, { value: '金华市', label: '金华市' }] },
        { value: '江苏省', label: '江苏省', children: [{ value: '南京市', label: '南京市' }, { value: '苏州市', label: '苏州市' }, { value: '无锡市', label: '无锡市' }, { value: '常州市', label: '常州市' }, { value: '南通市', label: '南通市' }] },
        { value: '山东省', label: '山东省', children: [{ value: '济南市', label: '济南市' }, { value: '青岛市', label: '青岛市' }, { value: '烟台市', label: '烟台市' }, { value: '潍坊市', label: '潍坊市' }, { value: '临沂市', label: '临沂市' }] },
        { value: '福建省', label: '福建省', children: [{ value: '福州市', label: '福州市' }, { value: '厦门市', label: '厦门市' }, { value: '泉州市', label: '泉州市' }, { value: '漳州市', label: '漳州市' }] },
        { value: '湖北省', label: '湖北省', children: [{ value: '武汉市', label: '武汉市' }, { value: '襄阳市', label: '襄阳市' }, { value: '宜昌市', label: '宜昌市' }] },
        { value: '湖南省', label: '湖南省', children: [{ value: '长沙市', label: '长沙市' }, { value: '株洲市', label: '株洲市' }, { value: '岳阳市', label: '岳阳市' }] },
        { value: '河南省', label: '河南省', children: [{ value: '郑州市', label: '郑州市' }, { value: '洛阳市', label: '洛阳市' }, { value: '南阳市', label: '南阳市' }] },
        { value: '河北省', label: '河北省', children: [{ value: '石家庄市', label: '石家庄市' }, { value: '唐山市', label: '唐山市' }, { value: '保定市', label: '保定市' }] },
        { value: '陕西省', label: '陕西省', children: [{ value: '西安市', label: '西安市' }, { value: '咸阳市', label: '咸阳市' }, { value: '榆林市', label: '榆林市' }] },
        { value: '辽宁省', label: '辽宁省', children: [{ value: '沈阳市', label: '沈阳市' }, { value: '大连市', label: '大连市' }, { value: '鞍山市', label: '鞍山市' }] },
        { value: '江西省', label: '江西省', children: [{ value: '南昌市', label: '南昌市' }, { value: '赣州市', label: '赣州市' }, { value: '九江市', label: '九江市' }] },
        { value: '安徽省', label: '安徽省', children: [{ value: '合肥市', label: '合肥市' }, { value: '芜湖市', label: '芜湖市' }, { value: '阜阳市', label: '阜阳市' }] }
      ],
      profile: {
        name: '星辰传媒有限公司',
        role: 'MCN机构',
        avatar: userAvatar,
        background: sidebarBg
      },
      menuItems: createMenu(),
      authForm: createEmptyAuthForm(),
      isUploading: false,
      isAgreementUploading: false,
      isSubmitting: false
    };
  },
  created() {
    this.authForm = this.normalizeFormData({ ...createEmptyAuthForm(), ...readLocal() });
    this.fetchPageData();
  },
  methods: {
    normalizeFormData(data) {
      const formData = { ...createEmptyAuthForm(), ...data };
      const businessLicenseUrl = pickFirstMeaningful(formData.businessLicenseUrl, formData.business_license, '');
      const legalPersonIdCardFront = pickFirstMeaningful(formData.legalPersonIdCardFront, formData.legal_person_id_card_front, '');
      const legalPersonIdCardBack = pickFirstMeaningful(formData.legalPersonIdCardBack, formData.legal_person_id_card_back, '');
      const agreementList = pickFirstMeaningful(formData.authorizationAgreements, formData.authorization_agreements, []);
      const legacyRegion = Array.isArray(formData.registerRegion) ? formData.registerRegion : [];
      const cityParts = String(formData.registerCity || '').split('/').map(item => item.trim()).filter(Boolean);
      const province = pickFirstMeaningful(formData.province, legacyRegion[0], cityParts[0], '');
      const city = pickFirstMeaningful(formData.city, legacyRegion[1], cityParts[1], '');

      return {
        ...formData,
        businessLicenseUrl,
        businessLicenseName: pickFirstMeaningful(formData.businessLicenseName, getFileName(businessLicenseUrl, '营业执照')),
        companyCode: pickFirstMeaningful(formData.companyCode, formData.company_code, formData.creditCode, ''),
        legalPersonName: pickFirstMeaningful(formData.legalPersonName, formData.legal_person_name, formData.legalPerson, ''),
        region: province && city ? [province, city] : [],
        province,
        city,
        addressDetail: pickFirstMeaningful(formData.addressDetail, formData.address_detail, formData.registerAddress, ''),
        registeredCapital: pickFirstMeaningful(formData.registeredCapital, formData.registered_capital, formData.registerCapital, ''),
        legalPersonIdCardFront,
        legalPersonIdCardFrontName: pickFirstMeaningful(formData.legalPersonIdCardFrontName, getFileName(legalPersonIdCardFront, '身份证正面')),
        legalPersonIdCardBack,
        legalPersonIdCardBackName: pickFirstMeaningful(formData.legalPersonIdCardBackName, getFileName(legalPersonIdCardBack, '身份证背面')),
        authorizationAgreements: normalizeAgreementItems(agreementList),
        status: pickFirstMeaningful(formData.status, '')
      };
    },
    async fetchPageData() {
      try {
        const [profileResponse, certificationResponse] = await Promise.all([
          getMemberProfile(),
          getMemberCertification()
        ]);
        this.applyProfileData(extractResponseData(profileResponse));
        this.applyCertificationData(extractResponseData(certificationResponse));
      } catch (error) {
        if (error && error.isAuthExpired) {
          return;
        }
        this.$message.error(error && error.message ? error.message : '获取机构认证信息失败');
      }
    },
    applyProfileData(profileData) {
      this.profile = {
        ...this.profile,
        name: profileData.name || this.profile.name,
        role: profileData.type_text || this.profile.role,
        avatar: profileData.avatar || this.profile.avatar
      };
    },
    applyCertificationData(certificationData) {
      const certificationFields = filterMeaningfulFields(certificationData);
      this.authForm = this.normalizeFormData({
        ...this.authForm,
        ...certificationFields,
        status: pickFirstMeaningful(certificationData.status, this.authForm.status, '')
      });
    },
    syncRegion() {
      this.authForm.province = Array.isArray(this.authForm.region) ? (this.authForm.region[0] || '') : '';
      this.authForm.city = Array.isArray(this.authForm.region) ? (this.authForm.region[1] || '') : '';
    },
    ensureEditable() {
      if (!this.isCertificationLocked) {
        return true;
      }
      this.$message.warning(`当前认证状态为${getCertificationStatusLabel(this.authForm.status)}`);
      return false;
    },
    isImageFile(value) {
      if (!value) {
        return false;
      }
      const source = String(value).split('?')[0].toLowerCase();
      return /\.(png|jpe?g|webp|gif|bmp)$/.test(source) || source.startsWith('data:image/');
    },
    triggerUpload(refName) {
      if (!this.ensureEditable()) {
        return;
      }
      const target = this.$refs[refName];
      if (target) {
        target.click();
      }
    },
    async handleSingleFileUpload(event, urlKey, nameKey, maxSizeMb) {
      const file = event.target.files && event.target.files[0];
      if (!file) {
        return;
      }

      if (!this.ensureEditable()) {
        event.target.value = '';
        return;
      }

      if (file.size > maxSizeMb * 1024 * 1024) {
        this.$message.warning(`文件大小不能超过${maxSizeMb}M`);
        event.target.value = '';
        return;
      }

      this.isUploading = true;
      this.authForm[nameKey] = '上传中...';

      try {
        const uploadResponse = await uploadMemberImage(file);
        const fileUrl = extractUploadUrl(uploadResponse);
        if (!fileUrl) {
          throw new Error('上传成功但未返回文件地址');
        }
        this.authForm[urlKey] = fileUrl;
        this.authForm[nameKey] = file.name;
        this.$message.success('文件上传成功');
      } catch (error) {
        this.authForm[urlKey] = '';
        this.authForm[nameKey] = '';
        this.$message.error(error && error.message ? error.message : '文件上传失败');
      } finally {
        this.isUploading = false;
        event.target.value = '';
      }
    },
    async handleAgreementUpload(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = '';

      if (!files.length) {
        return;
      }

      if (!this.ensureEditable()) {
        return;
      }

      const remain = 30 - this.authForm.authorizationAgreements.length;
      if (remain <= 0) {
        this.$message.warning('最多只能上传30份协议文件');
        return;
      }

      const allowExt = ['png', 'jpg', 'jpeg', 'pdf'];
      const selectedFiles = files.slice(0, remain);
      const invalidFile = selectedFiles.find(file => {
        const ext = String(file.name).split('.').pop().toLowerCase();
        return !allowExt.includes(ext) || file.size > 30 * 1024 * 1024;
      });

      if (invalidFile) {
        this.$message.warning('请上传 jpg、jpeg、png、pdf 格式且不超过30M的文件');
        return;
      }

      const uploadedUrls = new Set(this.authForm.authorizationAgreements.map(item => item.url));
      let successCount = 0;
      let failedCount = 0;
      this.isUploading = true;
      this.isAgreementUploading = true;

      try {
        for (let index = 0; index < selectedFiles.length; index += 1) {
          const file = selectedFiles[index];
          try {
            const uploadResponse = await uploadMemberFile(file, 'agreement');
            const fileUrl = extractUploadUrl(uploadResponse);
            if (!fileUrl || uploadedUrls.has(fileUrl)) {
              throw new Error('上传成功但未返回有效文件地址');
            }
            uploadedUrls.add(fileUrl);
            this.authForm.authorizationAgreements.push({ name: file.name, url: fileUrl });
            successCount += 1;
          } catch (error) {
            failedCount += 1;
          }
        }
      } finally {
        this.isUploading = false;
        this.isAgreementUploading = false;
      }

      if (successCount) {
        this.$message.success(`已上传${successCount}份协议文件`);
      }
      if (failedCount) {
        this.$message.warning(`${failedCount}份协议文件上传失败`);
      }
    },
    removeAgreement(index) {
      if (!this.ensureEditable()) {
        return;
      }
      this.authForm.authorizationAgreements.splice(index, 1);
    },
    validateForm() {
      const requiredFields = [
        ['businessLicenseUrl', '请上传营业执照'],
        ['companyCode', '请输入统一社会信用代码'],
        ['legalPersonName', '请输入法人姓名'],
        ['province', '请选择注册地址'],
        ['city', '请选择注册地址'],
        ['addressDetail', '请输入详细注册地址'],
        ['registeredCapital', '请输入注册资本'],
        ['legalPersonIdCardFront', '请上传身份证人像面'],
        ['legalPersonIdCardBack', '请上传身份证国徽面']
      ];

      for (let index = 0; index < requiredFields.length; index += 1) {
        const [field, message] = requiredFields[index];
        if (!this.authForm[field]) {
          this.$message.warning(message);
          return false;
        }
      }

      if (!this.authForm.authorizationAgreements.length) {
        this.$message.warning('请上传主播授权协议合同');
        return false;
      }

      return true;
    },
    saveDraft() {
      if (!this.ensureEditable()) {
        return;
      }
      this.syncRegion();
      saveLocal(this.authForm);
      this.$message.success('草稿已保存');
    },
    async submitAuth() {
      if (!this.ensureEditable() || !this.validateForm()) {
        return;
      }

      this.syncRegion();
      this.isSubmitting = true;

      try {
        await submitMcnCertification({
          business_license: this.authForm.businessLicenseUrl,
          company_code: this.authForm.companyCode,
          legal_person_name: this.authForm.legalPersonName,
          province: this.authForm.province,
          city: this.authForm.city,
          address_detail: this.authForm.addressDetail,
          registered_capital: this.authForm.registeredCapital,
          legal_person_id_card_front: this.authForm.legalPersonIdCardFront,
          legal_person_id_card_back: this.authForm.legalPersonIdCardBack,
          authorization_agreements: this.authForm.authorizationAgreements.map(item => item.url)
        });
        this.authForm.status = 'pending';
        saveLocal(this.authForm);
        this.$message.success('认证信息已提交');
        this.$router.push('/user/org');
      } catch (error) {
        this.$message.error(error && error.message ? error.message : '认证提交失败');
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.uploading-overlay {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.uploading-indicator {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 24px;
  border-radius: 8px;
  background: #ffffff;
  color: #303133;
  font-size: 15px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
}

.uploading-indicator .el-icon-loading {
  color: #6f68f3;
  font-size: 22px;
}

.back-row {
  display: flex;
  justify-content: flex-end;
  margin-top: -52px;
}

.back-btn {
  border: 0;
  background: transparent;
  font-weight: 400;
  font-size: 14px;
  color: #818181;
  line-height: 22px;
}

.form-section {
  margin-top: 24px;
}

.push-t {
  margin-top: 50px;
}

.form-section.compact {
  max-width: 420px;
}

.field-title {
  color: #3d3d3d;
  font-size: 16px;
  font-weight: 600;
}

.required {
  color: #ff6a6a;
}

.tip-text,
.sub-tip {
  font-family: Source Han Sans, Source Han Sans;
  font-weight: 400;
  font-size: 14px;
  color: #a1a1a1;
  line-height: 22px;
  margin-top: 8px;
}

.push-t-10 {
  margin-top: 10px;
}

.field-control {
  width: 308px;
  margin-top: 12px;
}

.field-control ::v-deep .el-input__inner {
  height: 42px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
}

.top-gap {
  margin-top: 10px;
}

.upload-license-card {
  position: relative;
  width: 90px;
  height: 90px;
  margin-top: 14px;
  border: 1px dashed #d8dfef;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8f97b3;
  font-size: 12px;
  cursor: pointer;
}

.upload-license-card.has-preview {
  padding: 0;
  border-style: solid;
  overflow: hidden;
}

.upload-license-preview {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.upload-license-card:disabled,
.upload-image-card:disabled,
.action-btn:disabled,
.agreement-remove:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.plus {
  font-size: 30px;
  color: #333;
  line-height: 1;
}

.upload-row {
  display: flex;
  gap: 14px;
  margin-top: 14px;
}

.upload-image-card {
  width: 144px;
  height: 112px;
  cursor: pointer;
  border: 0;
  background: #ffffff;
}

.upload-image-card img {
  width: 144px;
  height: 112px;
  object-fit: contain;
  display: block;
}

.agreement-list {
  margin-top: 14px;
  width: 420px;
}

.agreement-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 36px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}

.agreement-preview {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  background: #ffffff;
}

.agreement-item + .agreement-item {
  margin-top: 10px;
}

.agreement-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #3d3d3d;
  font-size: 14px;
}

.agreement-remove {
  border: 0;
  background: transparent;
  color: #5754cd;
  font-size: 14px;
}

.hidden-input {
  display: none;
}

.action-row {
  display: flex;
  justify-content: center;
  gap: 36px;
  margin-top: 60px;
}

.action-btn {
  min-width: 96px;
  height: 42px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
}

.action-btn.ghost {
  width: 86px;
  height: 40px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #d6d7d9;
  font-size: 13px;
  color: #606266;
}

.action-btn.primary {
  border: 0;
  color: #fff;
  width: 114px;
  height: 40px;
  background: #6f68f3;
  border-radius: 8px;
}

@media (max-width: 760px) {
  .field-control,
  .agreement-list {
    width: 100%;
  }

  .upload-row,
  .action-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
